import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Only the newest request may update the list, including after a manual refresh.
export function useAdminData(loader, params = {}) {
  const key = JSON.stringify(params);
  const requestId = useRef(0);
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: "",
    updatedAt: null,
  });
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const data = await loader(JSON.parse(key));
      if (id === requestId.current)
        setState({ data, loading: false, error: "", updatedAt: new Date() });
      return data;
    } catch (error) {
      if (id === requestId.current)
        setState((previous) => ({
          ...previous,
          loading: false,
          error: error.message || "Не удалось загрузить данные.",
        }));
      return null;
    }
  }, [loader, key]);

  useEffect(() => {
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    const timer = window.setTimeout(refresh, 250);
    return () => {
      window.clearTimeout(timer);
      requestId.current += 1;
    };
  }, [refresh]);

  const items = Array.isArray(state.data)
    ? state.data
    : state.data?.items || [];
  return { ...state, items, total: state.data?.total ?? items.length, refresh };
}

export function useAdminQuery(defaults = {}) {
  const [params, setParams] = useSearchParams();
  const values = { q: "", limit: "25", offset: "0", ...defaults };
  Object.keys(values).forEach((key) => {
    values[key] = params.get(key) ?? values[key];
  });
  values.limit = [25, 50, 100].includes(Number(values.limit))
    ? Number(values.limit)
    : 25;
  values.offset = Math.max(0, Number.parseInt(values.offset, 10) || 0);

  const update = (changes) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries({ offset: 0, ...changes }).forEach(([key, value]) => {
          if (
            value === "" ||
            value === null ||
            value === undefined ||
            (key === "offset" && Number(value) === 0)
          )
            next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      },
      { replace: true },
    );
  };
  const reset = () => update({ q: "", ...defaults, offset: 0 });
  return { values, update, reset };
}

export function useAdminTask() {
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = async (operation, onSuccess) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await operation();
      if (mounted.current) onSuccess?.(result);
    } catch (failure) {
      if (mounted.current)
        setError(
          failure.message ||
            "Не удалось выполнить действие. Попробуйте ещё раз.",
        );
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return { busy, error, setError, run };
}
